/**
 * DrawingToolsPanel
 * date_of_change: 2026-05-22
 * rule_reference: GAP-002 drawing layer — mirrors FogControls.js architecture exactly:
 *   standalone component, createPortal to document.body, panelRef + triggerRef for
 *   click-outside dismissal, useMemo panelStyle positioned below the trigger button.
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './DrawingToolsPanel.css';

// Stable tool list — defined at module scope so it never triggers hook deps.
// date_of_change: 2026-05-25 - Eraser renamed and moved to row 2 below Rectangle/Circle
const _TOOLS_ROW1 = [
  { id: 'freehand', label: 'Freehand' },
  { id: 'line',     label: 'Line' },
];

const _TOOLS_ROW2 = [
  { id: 'square',   label: 'Rectangle' },
  { id: 'circle',   label: 'Circle' },
  { id: 'eraser',   label: 'Eraser' },
];

const _COLORS = ['#ff0000', '#ff8800', '#ffff00', '#00cc44', '#00aaff', '#aa44ff', '#ffffff', '#000000'];

const DrawingToolsPanel = ({
  onClose,
  triggerRef,
  anchorRect,
  drawingEnabled   = false,
  onDrawingToggle,
  drawingTool      = 'freehand',
  onDrawingToolChange,
  drawingColor     = '#ff0000',
  onDrawingColorChange,
  drawingLineWidth = 3,
  onDrawingLineWidthChange,
  onDrawingUndo,
  onDrawingClear,
  canUndoDrawing   = false,
}) => {
  const panelRef = useRef(null);

  // Click-outside: close when pointer hits outside panel and trigger — same as FogControls.
  useEffect(() => {
    const handlePointerDown = (event) => {
      const insidePanel   = panelRef.current?.contains(event.target);
      const insideTrigger = triggerRef?.current?.contains?.(event.target);
      if (!insidePanel && !insideTrigger) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [onClose, triggerRef]);

  // Position below the trigger button — same formula as FogControls.
  const panelStyle = useMemo(() => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const left = Math.max(12, Math.min(anchorRect?.left ?? 12, viewportWidth - 252));
    const top  = Math.max((anchorRect?.bottom ?? 0) + 6, 12);
    return { position: 'fixed', top, left };
  }, [anchorRect]);

  const panel = (
    <div
      ref={panelRef}
      className="draw-panel"
      role="dialog"
      aria-label="Draw tools"
      style={panelStyle}
      data-testid="battle-toolbar-draw-flyout"
    >
      {/* Header */}
      <div className="draw-panel-header">
        <div className="draw-panel-title">
          <span aria-hidden="true">✏️</span>
          <span>Draw Tools</span>
        </div>
        <button
          type="button"
          className="draw-panel-close"
          onClick={onClose}
          aria-label="Close draw tools"
        >
          ×
        </button>
      </div>

      <div className="draw-panel-body">
        {/* Enable / disable drawing */}
        <button
          type="button"
          className={`draw-panel-toggle${drawingEnabled ? ' is-active' : ''}`}
          onClick={() => typeof onDrawingToggle === 'function' && onDrawingToggle()}
          aria-pressed={drawingEnabled}
          data-testid="draw-flyout-toggle"
        >
          {drawingEnabled ? 'Drawing ON' : 'Drawing OFF'}
        </button>

        {/* Tool selector - two rows: row 1 has freehand/line/erase, row 2 has rectangle/circle */}
        <div className="draw-panel-section">
          <span className="draw-panel-section-label">Tool</span>
          <div className="draw-panel-mode-group">
            {_TOOLS_ROW1.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`draw-panel-mode${drawingTool === t.id ? ' is-active' : ''}`}
                onClick={() => typeof onDrawingToolChange === 'function' && onDrawingToolChange(t.id)}
                aria-pressed={drawingTool === t.id}
                data-testid={`draw-tool-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="draw-panel-mode-group draw-panel-mode-group--row2">
            {_TOOLS_ROW2.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`draw-panel-mode draw-panel-mode--wide${drawingTool === t.id ? ' is-active' : ''}`}
                onClick={() => typeof onDrawingToolChange === 'function' && onDrawingToolChange(t.id)}
                aria-pressed={drawingTool === t.id}
                data-testid={`draw-tool-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Color swatches */}
        <div className="draw-panel-section">
          <span className="draw-panel-section-label">Color</span>
          <div className="draw-panel-colors">
            {_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className={`draw-panel-color-btn${drawingColor === c ? ' is-active' : ''}`}
                style={{ background: c }}
                onClick={() => typeof onDrawingColorChange === 'function' && onDrawingColorChange(c)}
                aria-label={`Color ${c}`}
                data-testid={`draw-color-${c.replace('#', '')}`}
              />
            ))}
          </div>
        </div>

        {/* Line width - numeric input with up/down arrows */}
        <div className="draw-panel-section">
          <span className="draw-panel-section-label">Width</span>
          <div className="draw-panel-width-input">
            <input
              type="number"
              min="1"
              max="50"
              value={drawingLineWidth}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 50 && typeof onDrawingLineWidthChange === 'function') {
                  onDrawingLineWidthChange(val);
                }
              }}
              className="draw-panel-width-field"
              aria-label="Line width"
            />
          </div>
        </div>

        {/* Undo / Clear */}
        <div className="draw-panel-action-row">
          <button
            type="button"
            className="draw-panel-action is-undo"
            disabled={!canUndoDrawing}
            onClick={() => typeof onDrawingUndo === 'function' && onDrawingUndo()}
            data-testid="draw-flyout-undo"
          >
            Undo
          </button>
          <button
            type="button"
            className="draw-panel-action is-clear"
            onClick={() => typeof onDrawingClear === 'function' && onDrawingClear()}
            data-testid="draw-flyout-clear"
          >
            Erase All
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return panel;
  }

  return createPortal(panel, document.body);
};

DrawingToolsPanel.propTypes = {
  onClose:               PropTypes.func.isRequired,
  triggerRef:            PropTypes.shape({ current: PropTypes.any }),
  anchorRect:            PropTypes.shape({
    top:    PropTypes.number,
    left:   PropTypes.number,
    bottom: PropTypes.number,
    right:  PropTypes.number,
    width:  PropTypes.number,
    height: PropTypes.number,
  }),
  drawingEnabled:        PropTypes.bool,
  onDrawingToggle:       PropTypes.func,
  drawingTool:           PropTypes.oneOf(['freehand', 'lasso', 'line', 'square', 'circle', 'eraser']),
  onDrawingToolChange:   PropTypes.func,
  drawingColor:          PropTypes.string,
  onDrawingColorChange:  PropTypes.func,
  drawingLineWidth:      PropTypes.number,
  onDrawingLineWidthChange: PropTypes.func,
  onDrawingUndo:         PropTypes.func,
  onDrawingClear:        PropTypes.func,
  canUndoDrawing:        PropTypes.bool,
};

export default DrawingToolsPanel;
